import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import RefreshIcon from '@mui/icons-material/Refresh'
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1'
import { approveUser, getGoogleUsers } from '../../services/UserAdmin'
import './UserApproval.css'

const formatDate = (value) => {
  if (!value) return '-'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'

  return new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}

const UserApproval = () => {
  const queryClient = useQueryClient()
  const [selectedUser, setSelectedUser] = useState(null)
  const [successMessage, setSuccessMessage] = useState('')

  const usersQuery = useQuery({
    queryKey: ['google-users'],
    queryFn: getGoogleUsers,
    refetchInterval: 5000,
    refetchIntervalInBackground: true
  })

  useEffect(() => {
    if (!successMessage) return undefined

    const timer = window.setTimeout(() => setSuccessMessage(''), 2000)
    return () => window.clearTimeout(timer)
  }, [successMessage])

  const approveMutation = useMutation({
    mutationFn: approveUser,
    onSuccess: (approvedUserData, userId) => {
      const approvedUser = usersQuery.data?.data?.find(
        (user) => user.id === userId
      )
      setSuccessMessage(`${approvedUser?.email || 'ユーザー'}を承認しました。`)
      setSelectedUser(null)
      queryClient.setQueryData(['google-users'], (currentData) => {
        if (!currentData) return currentData
        return {
          ...currentData,
          pending_count: Math.max((currentData.pending_count || 1) - 1, 0),
          data: currentData.data.map((user) =>
            user.id === userId
              ? {
                  ...user,
                  active: true,
                  approved_at: approvedUserData.approved_at
                }
              : user
          )
        }
      })
      queryClient.invalidateQueries({ queryKey: ['google-users'] })
    }
  })

  const googleUsers = usersQuery.data?.data || []

  const handleRefresh = () => {
    setSuccessMessage('')
    usersQuery.refetch()
  }

  const handleApprove = () => {
    if (!selectedUser || approveMutation.isPending) return
    setSuccessMessage('')
    approveMutation.mutate(selectedUser.id)
  }

  return (
    <main className='ua-page'>
      <div className='ua-header'>
        <div>
          <h1>ユーザー承認</h1>
          <p>Googleログインを申請したユーザーを確認・承認します。</p>
        </div>
        <button
          type='button'
          className='ua-refresh-button'
          onClick={handleRefresh}
          disabled={usersQuery.isFetching}
        >
          <RefreshIcon fontSize='small' />
          更新
        </button>
      </div>

      {successMessage && (
        <div className='ua-message ua-message-success' role='status'>
          {successMessage}
        </div>
      )}

      {(usersQuery.error || approveMutation.error) && (
        <div className='ua-message ua-message-error' role='alert'>
          {usersQuery.error?.message ||
            approveMutation.error?.message ||
            '操作に失敗しました。'}
        </div>
      )}

      <section className='ua-panel' aria-busy={usersQuery.isLoading}>
        <div className='ua-panel-heading'>
          <h2>Googleログインユーザー</h2>
          <span className='ua-count'>{usersQuery.data?.count ?? 0} 件</span>
        </div>

        {usersQuery.isLoading ? (
          <div className='ua-state'>読み込み中...</div>
        ) : googleUsers.length === 0 ? (
          <div className='ua-state ua-empty-state'>
            <CheckCircleOutlineIcon />
            <strong>Googleログインユーザーはいません</strong>
          </div>
        ) : (
          <div className='ua-table-wrap'>
            <table className='ua-table'>
              <thead>
                <tr>
                  <th>ユーザー</th>
                  <th>メールアドレス</th>
                  <th>申請日時</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {googleUsers.map((user) => {
                  const isApproved = Boolean(user.active)

                  return (
                    <tr
                      key={user.id}
                      className={isApproved ? 'ua-approved-row' : ''}
                    >
                      <td data-label='ユーザー'>
                        <span className='ua-user-name'>
                          {user.username || user.email.split('@')[0]}
                        </span>
                      </td>
                      <td data-label='メールアドレス'>{user.email}</td>
                      <td data-label='申請日時'>
                        {formatDate(user.created_at)}
                      </td>
                      <td data-label='操作'>
                        {isApproved ? (
                          <span className='ua-approved-status'>
                            <CheckCircleOutlineIcon fontSize='small' />
                            承認済み
                          </span>
                        ) : (
                          <button
                            type='button'
                            className='ua-approve-button'
                            onClick={() => setSelectedUser(user)}
                          >
                            <PersonAddAlt1Icon fontSize='small' />
                            承認
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {selectedUser && (
        <div
          className='ua-dialog-backdrop'
          role='presentation'
          onMouseDown={(event) => {
            if (
              event.target === event.currentTarget &&
              !approveMutation.isPending
            ) {
              setSelectedUser(null)
            }
          }}
        >
          <div
            className='ua-dialog'
            role='dialog'
            aria-modal='true'
            aria-labelledby='approve-dialog-title'
          >
            <h2 id='approve-dialog-title'>ユーザーを承認しますか？</h2>
            <p>
              <strong>{selectedUser.email}</strong>
              <br />
              承認後、このユーザーはGoogleアカウントでログインできます。
            </p>
            <div className='ua-dialog-actions'>
              <button
                type='button'
                className='ua-cancel-button'
                onClick={() => setSelectedUser(null)}
                disabled={approveMutation.isPending}
              >
                キャンセル
              </button>
              <button
                type='button'
                className='ua-confirm-button'
                onClick={handleApprove}
                disabled={approveMutation.isPending}
              >
                {approveMutation.isPending ? '承認中...' : '承認する'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  )
}

export default UserApproval
